// modulePartProgress.js — which parts of a block-format module a reader has
// finished, so they come back to the first unfinished one.
//
// Why this exists: AppContext.updateModuleProgress goes through the storage
// service, whose save() silently returns false when nobody is signed in —
// so a guest's progress never reaches localStorage at all. The renderer
// still records progress through AppContext (signed-in readers get the
// synced copy) and keeps this local copy as well, reading the union.
//
// Key shape mirrors the storage service's namespace so the app's own
// sign-out wipe (`friendships-vibe_<uid>_*`) and global clear
// (`friendships-vibe_*`) remove it too — nothing outlives the account.
const NAMESPACE = 'friendships-vibe';

const storageKey = () => {
  try {
    const raw = localStorage.getItem('vibe_user_identity');
    const userId = raw ? JSON.parse(raw)?.userId : null;
    return userId || 'guest';
  } catch {
    return 'guest';
  }
};

const keyFor = (moduleId) => `${NAMESPACE}_${storageKey()}_module-parts_${moduleId}`;
const positionKeyFor = (moduleId) => `${NAMESPACE}_${storageKey()}_module-position_${moduleId}`;

export const readCompletedParts = (moduleId) => {
  try {
    const raw = localStorage.getItem(keyFor(moduleId));
    const parts = raw ? JSON.parse(raw) : [];
    return Array.isArray(parts) ? parts : [];
  } catch {
    return [];
  }
};

export const writeCompletedParts = (moduleId, parts) => {
  try {
    localStorage.setItem(keyFor(moduleId), JSON.stringify(Array.from(parts)));
  } catch {
    // Storage unavailable (private mode, quota) — progress just won't resume.
  }
};

// Where the reader actually was — which view and how far down it — so that
// leaving for a tool (or the dashboard, or a closed tab) and coming back
// lands them on the same paragraph rather than the start of the module.
export const readModulePosition = (moduleId) => {
  try {
    const raw = localStorage.getItem(positionKeyFor(moduleId));
    const pos = raw ? JSON.parse(raw) : null;
    return pos && typeof pos === 'object' && pos.viewId != null ? pos : null;
  } catch {
    return null;
  }
};

export const writeModulePosition = (moduleId, { viewId, scrollY = 0 }) => {
  if (viewId == null) return;
  try {
    localStorage.setItem(
      positionKeyFor(moduleId),
      JSON.stringify({ viewId, scrollY: Math.max(0, Math.round(scrollY || 0)), at: Date.now() }),
    );
  } catch {
    // Storage unavailable — they start from the first unfinished part instead.
  }
};
